import { parseDateOnlyAsUTC } from "@common/helpers/date";
import { isDateOnlyInReconciledPeriod } from "@common/helpers/reconciliation";
import { getCashItemInReconciledPeriodMessage } from "@common/helpers/reconciliationCashItems";
import { createCashItemBodySchema } from "@common/schemas/domains/cashItem/cashItem.schema";
import { BankCashItemType } from "@common/types/bankCashItem";
import {
    fromIntToMonetary,
    fromMonetaryToInt,
    monetaryValuesEqual,
    sumMonetaryValues,
} from "@common/helpers/monetary";
import { validateFormikWithZodSchema } from "@helpers/form";
import {
    amountToPercent,
    percentToAmount,
    recalculateParentAmount,
    SplitRow,
    syncRowPercentages,
} from "@components/transactions/TransactionSplitV2/transactionSplitUtils";

export const PARENT_CHECK_LINE_ID = 0;

export interface RecordCheckLine {
    description: string;
    categoryId: string;
    accountId: string;
    accrualAccountId: string;
    classIds: string[];
    amount?: number;
}

export interface RecordCheckFormValues {
    type: BankCashItemType;
    financialAccountId?: number;
    date: string;
    amount?: number;
    checkNumber: string;
    counterpartyId: string;
    memo: string;
    lines: RecordCheckLine[];
}

export function emptyCheckLine(): RecordCheckLine {
    return {
        description: "",
        categoryId: "",
        accountId: "",
        accrualAccountId: "",
        classIds: [],
    };
}

export function isCheckLineCoded(
    line: Pick<RecordCheckLine, "categoryId" | "accountId">,
    isGlFirst: boolean,
): boolean {
    if (isGlFirst) {
        return line.accountId !== "";
    }
    return line.categoryId !== "" || line.accountId !== "";
}

export function checkLineAmounts(
    lines: RecordCheckLine[],
    checkAmount: number,
): number[] {
    const childTotal = sumMonetaryValues(
        lines.slice(1).map((line) => line.amount ?? 0),
    );
    const parentAmount = fromIntToMonetary(
        fromMonetaryToInt(checkAmount) - fromMonetaryToInt(childTotal),
    );
    return [parentAmount, ...lines.slice(1).map((line) => line.amount ?? 0)];
}

export function hasEnteredCashItemDetails({
    values,
    initialDate,
}: {
    values: RecordCheckFormValues;
    initialDate: string;
}): boolean {
    return (
        values.date !== initialDate ||
        values.amount != null ||
        values.checkNumber.trim() !== "" ||
        values.counterpartyId !== "" ||
        values.memo.trim() !== "" ||
        values.lines.some(
            (line) =>
                line.description.trim() !== "" ||
                line.categoryId !== "" ||
                line.accountId !== "" ||
                line.classIds.length > 0 ||
                line.amount != null,
        )
    );
}

interface GetRecordCheckDateErrorParams {
    values: Pick<RecordCheckFormValues, "type" | "date">;
    lastReconciledClosingDate?: string | null;
}

interface ValidateRecordCheckFormParams {
    values: RecordCheckFormValues;
    lastReconciledClosingDate?: string | null;
}

interface CanSubmitRecordCheckParams {
    values: RecordCheckFormValues;
    isGlFirst: boolean;
    lastReconciledClosingDate?: string | null;
}

export function getRecordCheckDateError({
    values,
    lastReconciledClosingDate,
}: GetRecordCheckDateErrorParams): string | undefined {
    if (
        values.date === "" ||
        lastReconciledClosingDate == null ||
        !isDateOnlyInReconciledPeriod({
            dateOnly: values.date,
            previousClosingDate: lastReconciledClosingDate,
        })
    ) {
        return undefined;
    }
    return getCashItemInReconciledPeriodMessage({
        type: values.type,
        lastReconciledClosingDate,
    });
}

export function validateRecordCheckForm({
    values,
    lastReconciledClosingDate,
}: ValidateRecordCheckFormParams) {
    const errors = validateFormikWithZodSchema(createCashItemBodySchema)(
        toCreateCheckBody(values),
    );
    const dateError = getRecordCheckDateError({
        values,
        lastReconciledClosingDate,
    });
    return dateError ? { ...errors, date: dateError } : errors;
}

export function canSubmitRecordCheck({
    values,
    isGlFirst,
    lastReconciledClosingDate,
}: CanSubmitRecordCheckParams): boolean {
    if (values.financialAccountId == null) {
        return false;
    }
    if (values.date === "") {
        return false;
    }
    if (
        getRecordCheckDateError({ values, lastReconciledClosingDate }) !==
        undefined
    ) {
        return false;
    }
    if (values.amount == null || values.amount <= 0) {
        return false;
    }
    if (values.counterpartyId === "") {
        return false;
    }
    if (values.lines.length === 0) {
        return false;
    }
    if (values.lines.some((line) => !isCheckLineCoded(line, isGlFirst))) {
        return false;
    }
    const amounts = checkLineAmounts(values.lines, values.amount);
    if (amounts.some((amount) => amount < 0)) {
        return false;
    }
    return monetaryValuesEqual(values.amount, sumMonetaryValues(amounts));
}

export function addCheckSplit(
    lines: RecordCheckLine[],
    checkAmount: number,
): RecordCheckLine[] {
    const parent = lines[0] ?? emptyCheckLine();
    const amounts = checkLineAmounts(lines, checkAmount);
    const current = lines.map((line, index) => ({
        ...line,
        amount: amounts[index],
    }));
    const newCount = current.length + 1;
    const firstAmount = current[0]?.amount;
    const isSymmetric = current.every((line) => line.amount === firstAmount);

    let rows: SplitRow[];
    if (isSymmetric) {
        const baseAmount = fromIntToMonetary(
            Math.floor(fromMonetaryToInt(checkAmount) / newCount),
        );
        rows = [
            ...current.map((line, index) => ({
                _id: index === 0 ? PARENT_CHECK_LINE_ID : undefined,
                amount: baseAmount,
                splitDescription: line.description,
                categoryId: line.categoryId,
                classIds: line.classIds,
                accountId: line.accountId,
                accrualAccountId: line.accrualAccountId,
            })),
            {
                categoryId: parent.categoryId,
                classIds: parent.classIds,
                accountId: parent.accountId,
                accrualAccountId: parent.accrualAccountId,
                amount: baseAmount,
            },
        ];
    } else {
        const newSplitAmount = fromIntToMonetary(
            Math.floor(fromMonetaryToInt(amounts[0] ?? 0) / 2),
        );
        rows = [
            ...current.map((line, index) => ({
                _id: index === 0 ? PARENT_CHECK_LINE_ID : undefined,
                amount: line.amount,
                splitDescription: line.description,
                categoryId: line.categoryId,
                classIds: line.classIds,
                accountId: line.accountId,
                accrualAccountId: line.accrualAccountId,
            })),
            {
                categoryId: parent.categoryId,
                classIds: parent.classIds,
                accountId: parent.accountId,
                accrualAccountId: parent.accrualAccountId,
                amount: newSplitAmount,
            },
        ];
    }

    rows = recalculateParentAmount(rows, PARENT_CHECK_LINE_ID, checkAmount);
    rows = syncRowPercentages(rows, checkAmount);
    return rowsToCheckLines(rows, current);
}

export function applyCheckLineAmountEdit(params: {
    lines: RecordCheckLine[];
    rowIndex: number;
    amount: number;
    checkAmount: number;
}): RecordCheckLine[] {
    const next = params.lines.map((line, index) =>
        index === params.rowIndex ? { ...line, amount: params.amount } : line,
    );
    const rows = next.map((line, index) => ({
        _id: index === 0 ? PARENT_CHECK_LINE_ID : undefined,
        amount: index === 0 ? 0 : (line.amount ?? 0),
        splitDescription: line.description,
        categoryId: line.categoryId,
        classIds: line.classIds,
        accountId: line.accountId,
        accrualAccountId: line.accrualAccountId,
    }));
    const withRemainder = recalculateParentAmount(
        rows,
        PARENT_CHECK_LINE_ID,
        params.checkAmount,
    );
    return rowsToCheckLines(withRemainder, next);
}

export function applyCheckLinePercentEdit(params: {
    lines: RecordCheckLine[];
    rowIndex: number;
    percent: number;
    checkAmount: number;
}): RecordCheckLine[] {
    return applyCheckLineAmountEdit({
        lines: params.lines,
        rowIndex: params.rowIndex,
        amount: percentToAmount(params.percent, params.checkAmount),
        checkAmount: params.checkAmount,
    });
}

export function toCreateCheckBody(values: RecordCheckFormValues) {
    const amounts = checkLineAmounts(values.lines, values.amount ?? 0);

    return {
        financialAccountId: values.financialAccountId,
        date: values.date ? parseDateOnlyAsUTC(values.date) : undefined,
        amount: values.amount,
        checkNumber: values.checkNumber,
        counterpartyId: values.counterpartyId || undefined,
        memo: values.memo || null,
        splits: values.lines.map((line, index) => ({
            amount: amounts[index],
            categoryId: line.categoryId || undefined,
            accountId: line.accountId || undefined,
            accrualAccountId: line.accrualAccountId || undefined,
            description: line.description.trim() || undefined,
            classIds: line.classIds.length > 0 ? line.classIds : undefined,
        })),
    };
}

function rowsToCheckLines(
    rows: SplitRow[],
    previous: RecordCheckLine[],
): RecordCheckLine[] {
    return rows.map((row, index) => ({
        description: row.splitDescription ?? previous[index]?.description ?? "",
        categoryId: row.categoryId ?? previous[index]?.categoryId ?? "",
        accountId: row.accountId ?? previous[index]?.accountId ?? "",
        accrualAccountId:
            row.accrualAccountId ?? previous[index]?.accrualAccountId ?? "",
        classIds: row.classIds ?? previous[index]?.classIds ?? [],
        amount: row.amount,
    }));
}

export { amountToPercent };
